import { useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import { classNames } from "Store/utils";
import "TrustEdge.css";
import "./ReviewSlider.css";

const ReviewSlider = ({ reviews }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [expandedCards, setExpandedCards] = useState(new Set());

  const toggleReadMore = (index) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const openVideoModal = (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentVideoUrl("");
  };

  const CustomArrow = ({ direction, onClick }) => (
    <button
      className={`custom-arrow custom-arrow-${direction}`}
      onClick={onClick}
    >
      {direction === "prev" ? "‹" : "›"}
    </button>
  );

  const settings = {
    infinite: true,
    arrows: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    speed: 600,
    cssEase: "cubic-bezier(0.4, 0, 0.2, 1)",
    waitForAnimate: false,
    swipeToSlide: true,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    prevArrow: <CustomArrow direction="prev" />,
    nextArrow: <CustomArrow direction="next" />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          arrows: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: true,
          dots: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
          centerMode: false,
          variableWidth: false,
        },
      },
    ],
  };

  return (
    <>
      <div className="review-slider-wrapper">
        <Slider {...settings}>
          {reviews.map((review, index) => {
            const isExpanded = expandedCards.has(index);

            return (
              <div key={index}>
                <div style={{ padding: "0 12px" }}>
                  <div className="review-card">
                    {/* Decorative gradient overlay */}
                    <div className="review-card-gradient" />

                    {/* Video Section */}
                    {review.video_src && (
                      <div
                        className="video-container"
                        onClick={() => openVideoModal(review.video_src)}
                      >
                        <video
                          className="video-element"
                          muted
                          preload="metadata"
                          poster={review?.poster}
                        >
                          <source src={review.video_src} type="video/mp4" />
                        </video>

                        {/* Play button overlay */}
                        <div className="video-play-button">
                          <FontAwesomeIcon
                            icon={faPlay}
                            className="video-play-icon"
                          />
                        </div>
                      </div>
                    )}

                    {/* Content Section */}
                    <div className="review-content">
                      {/* Quote mark for text reviews */}
                      {review?.description && !review.video_src && (
                        <div className="quote-mark">
                          "
                        </div>
                      )}

                      {/* Description */}
                      {review?.description && (
                        <div className="description-container">
                          <p
                            className={classNames(
                              "description-text",
                              review.video_src ? "description-text-normal" : "description-text-italic",
                              isExpanded
                                ? "description-text-expanded"
                                : review.video_src
                                  ? "description-text-collapsed-video"
                                  : "description-text-collapsed"
                            )}
                          >
                            {review.video_src ? `"${review.description}"` : review.description}
                          </p>

                          {!review.video_src && review.description.length > 200 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleReadMore(index);
                              }}
                              className="read-more-button"
                            >
                              {isExpanded ? "Show Less" : "Read More"}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Author Info */}
                      <div className="author-info">
                        <div className="author-container">
                          {/* Avatar */}
                          <div className="author-avatar">
                            {review.name ? review.name.charAt(0).toUpperCase() : "U"}
                          </div>

                          <div>
                            <div className="author-name">
                              {review.name}
                            </div>
                            <div className="author-college">
                              {review.college}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </Slider>
      </div>

      {/* Video Modal */}
      <Modal
        show={isModalOpen}
        onHide={closeModal}
        centered
        size="lg"
        backdrop="static"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton className="video-modal-header">
          <Modal.Title className="video-modal-title">
            Video Review
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="video-modal-body">
          <div className="video-modal-container">
            <video controls autoPlay className="video-modal-element">
              <source src={currentVideoUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ReviewSlider;